/**
 * Push Notifications Utility for iOS/Android Native Apps
 * 
 * Handles push notification registration, permissions, and listeners.
 * Only active on native platforms (iOS/Android).
 */

import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

const isNative = Capacitor.isNativePlatform();

// Android push requires Firebase (android/app/google-services.json). Calling
// PushNotifications.register() without it crashes the app natively
// ("Default FirebaseApp is not initialized"). Flip to true ONLY after adding
// google-services.json to the Android project.
const ANDROID_PUSH_ENABLED = false;

const isAndroidPushBlocked = () =>
  Capacitor.getPlatform() === 'android' && !ANDROID_PUSH_ENABLED;

// Store the device token for sending to backend
let deviceToken = null;

/**
 * Get the current device token (if registered)
 */
export const getDeviceToken = () => deviceToken;

/**
 * Request permission and register for push notifications
 * Returns the device token if successful
 */
export const registerForPushNotifications = async () => {
  if (!isNative) {
    return null;
  }

  // Skip entirely on Android until Firebase is configured — requesting the
  // permission then registering crashes the app (no google-services.json).
  if (isAndroidPushBlocked()) {
    return false;
  }

  try {
    // Request permission
    const permResult = await PushNotifications.requestPermissions();
    
    if (permResult.receive === 'granted') {
      // Register with Apple/Google push services
      await PushNotifications.register();
      return true;
    } else {
      return false;
    }
  } catch (error) {
    console.error('Error registering for push notifications:', error);
    return false;
  }
};

/**
 * Check if push notifications are enabled
 */
export const checkPushPermissions = async () => {
  if (!isNative) return { receive: 'unavailable' };
  if (isAndroidPushBlocked()) return { receive: 'unavailable' };

  try {
    return await PushNotifications.checkPermissions();
  } catch (error) {
    console.error('Error checking push permissions:', error);
    return { receive: 'denied' };
  }
};

/**
 * Initialize push notification listeners
 * Call this once when the app starts
 */
export const initPushNotifications = (callbacks = {}) => {
  if (!isNative) return;
  if (isAndroidPushBlocked()) return;

  const {
    onRegistration,
    onRegistrationError,
    onNotificationReceived,
    onNotificationAction,
  } = callbacks;

  // Registration success - got device token
  PushNotifications.addListener('registration', (token) => {
    deviceToken = token.value;
    
    if (onRegistration) {
      onRegistration(token.value);
    }
  });

  // Registration error
  PushNotifications.addListener('registrationError', (error) => {
    console.error('Push registration error:', error);
    
    if (onRegistrationError) {
      onRegistrationError(error);
    }
  });

  // Notification received while app is in foreground
  PushNotifications.addListener('pushNotificationReceived', (notification) => {
    
    if (onNotificationReceived) {
      onNotificationReceived(notification);
    }
  });

  // User tapped on a notification
  PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    
    if (onNotificationAction) {
      onNotificationAction(action);
    }
  });
};

/**
 * Remove all push notification listeners
 * Call when unmounting
 */
export const removePushListeners = async () => {
  if (!isNative) return;
  
  try {
    await PushNotifications.removeAllListeners();
  } catch (error) {
    console.error('Error removing push listeners:', error);
  }
};

/**
 * Send device token to backend for push notification targeting
 */
export const registerDeviceWithBackend = async (userId, orgId, backendUrl, jwtToken) => {
  if (!deviceToken) {
    return false;
  }

  try {
    const response = await fetch(`${backendUrl}/api/push-notifications/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${jwtToken}`,
      },
      body: JSON.stringify({
        deviceToken,
        platform: Capacitor.getPlatform(), // 'ios' or 'android'
        userId,
        orgId,
      }),
    });

    if (response.ok) {
      return true;
    } else {
      console.error('Failed to register device with backend');
      return false;
    }
  } catch (error) {
    console.error('Error registering device with backend:', error);
    return false;
  }
};

export default {
  register: registerForPushNotifications,
  checkPermissions: checkPushPermissions,
  init: initPushNotifications,
  removeListeners: removePushListeners,
  getToken: getDeviceToken,
  registerWithBackend: registerDeviceWithBackend,
};
